import { FormEvent, KeyboardEvent as ReactKeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { FleetHealthSummary, ManualIssueListItem, SiteHealthSummary, SiteWithConfig } from '@accestra/types';
import { api, ApiError } from '../lib/api-client';
import { getPlanLabel } from '../lib/plans';
import {
  estimateAccessibilityScore,
  getScoreLabelKey,
  getScoreVariant,
} from '../lib/accessibility-score';
import Button from '../components/ui/Button';
import Badge, { type BadgeVariant } from '../components/ui/Badge';
import Card from '../components/ui/Card';
import Container from '../components/ui/Container';
import EmptyState from '../components/ui/EmptyState';
import PageHeader from '../components/ui/PageHeader';
import SectionHeader from '../components/ui/SectionHeader';
import StatCard from '../components/ui/StatCard';
import { formatDateTime } from '../i18n/formatters';
import ManualIssueModal, { type ManualIssueModalSubmitInput } from '../components/manual-issues/ManualIssueModal';
import ManualIssuesTable from '../components/manual-issues/ManualIssuesTable';
import { dispatchWorkspaceDataChanged, subscribeToWorkspaceDataChanges } from '../lib/workspace-data-events';
import { useAuth } from '../hooks/useAuth';

interface SitesResponse {
  sites: SiteWithConfig[];
  plan?: {
    plan: string;
    limits: {
      maxSites: number;
      maxPagesPerScan: number;
    };
  };
}

interface LatestScan {
  id: string;
  status: string;
  totalIssues: number;
  criticalIssues: number;
  pagesScanned: number;
  completedAt: string | null;
  createdAt: string;
  failedAt?: string | null;
}

interface HealthSummaryResponse {
  summary: FleetHealthSummary;
}

interface ManualIssuesResponse {
  issues: ManualIssueListItem[];
}

type ScanMap = Record<string, LatestScan | null>;

type SitePortfolioView =
  | 'portfolio'
  | 'queue'
  | 'issues'
  | 'evidence'
  | 'auth'
  | 'schedules'
  | 'reports';

type SiteRow = {
  site: SiteWithConfig;
  scan: LatestScan | null;
  health: SiteHealthSummary | null;
  score: number | null;
  authLabel: string;
  authVariant: BadgeVariant;
  trendLabel: string;
  trendVariant: BadgeVariant;
  quickActionLabel: string;
  quickActionTo: string;
};

const C = {
  text: 'var(--acc-color-text)',
  muted: 'var(--acc-color-text-muted)',
  border: 'var(--acc-color-border)',
  link: 'var(--acc-color-primary)',
  surface: 'var(--acc-color-surface)',
  surfaceMuted: 'var(--acc-color-surface-muted)',
  soft: 'var(--acc-color-text-soft)',
};

const TABLE = {
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
  },
  th: {
    textAlign: 'left' as const,
    padding: '14px 18px',
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: '0.12em',
    textTransform: 'uppercase' as const,
    color: 'var(--acc-color-text-soft)',
    borderBottom: '1px solid var(--acc-color-border)',
    background: 'var(--acc-color-surface-muted)',
  },
  td: {
    padding: '16px 18px',
    borderBottom: '1px solid var(--acc-color-border)',
    verticalAlign: 'top' as const,
  },
};

const MODAL_FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getFocusableElements(container: HTMLElement | null): HTMLElement[] {
  if (!container) {
    return [];
  }

  return Array.from(container.querySelectorAll<HTMLElement>(MODAL_FOCUSABLE_SELECTOR)).filter((element) => {
    if (element.getAttribute('aria-hidden') === 'true') {
      return false;
    }

    if (element instanceof HTMLInputElement && element.type === 'hidden') {
      return false;
    }

    return element.getClientRects().length > 0;
  });
}

function getViewMeta(t: TFunction): Record<SitePortfolioView, { label: string; description: string }> {
  return {
    portfolio: {
      label: t('views.portfolio.label'),
      description: t('views.portfolio.description'),
    },
    queue: {
      label: t('views.queue.label'),
      description: t('views.queue.description'),
    },
    issues: {
      label: t('views.issues.label'),
      description: t('views.issues.description'),
    },
    evidence: {
      label: t('views.evidence.label'),
      description: t('views.evidence.description'),
    },
    auth: {
      label: t('views.auth.label'),
      description: t('views.auth.description'),
    },
    schedules: {
      label: t('views.schedules.label'),
      description: t('views.schedules.description'),
    },
    reports: {
      label: t('views.reports.label'),
      description: t('views.reports.description'),
    },
  };
}

function scanStatusVariant(status: string): BadgeVariant {
  switch (status) {
    case 'completed':
      return 'success';
    case 'completed_with_failures':
    case 'running':
      return 'warning';
    case 'queued':
      return 'info';
    case 'failed':
    case 'auth_failed':
      return 'danger';
    default:
      return 'neutral';
  }
}

function authVariant(state: SiteHealthSummary['authState'] | null): BadgeVariant {
  switch (state) {
    case 'healthy':
      return 'success';
    case 'reauth_required':
      return 'warning';
    case 'auth_failed':
      return 'danger';
    case 'not_configured':
    default:
      return 'neutral';
  }
}

function authLabel(state: SiteHealthSummary['authState'] | null, t: TFunction): string {
  switch (state) {
    case 'healthy':
      return t('authState.healthy');
    case 'reauth_required':
      return t('authState.reauthRequired');
    case 'auth_failed':
      return t('authState.authFailed');
    case 'not_configured':
      return t('authState.publicOnly');
    default:
      return t('authState.unknown');
  }
}

function trendVariant(state: SiteHealthSummary['latestRegressionState']): BadgeVariant {
  switch (state) {
    case 'regressed':
      return 'danger';
    case 'improved':
      return 'success';
    case 'baseline':
      return 'info';
    case 'stable':
    default:
      return 'neutral';
  }
}

function trendLabel(state: SiteHealthSummary['latestRegressionState'], t: TFunction): string {
  switch (state) {
    case 'regressed':
      return t('trend.regressed');
    case 'improved':
      return t('trend.improved');
    case 'baseline':
      return t('trend.baseline');
    case 'stable':
    default:
      return t('trend.stable');
  }
}

function healthSummaryLabel(health: SiteHealthSummary | null, t: TFunction): string {
  if (!health) {
    return t('table.authPending');
  }
  if (health.authState === 'auth_failed') {
    return t('summary.authFailed');
  }
  if (health.authState === 'reauth_required') {
    return t('summary.reauthRequired');
  }
  if (health.openCriticalIssues > 0) {
    return t('summary.criticalIssues', { count: health.openCriticalIssues });
  }
  if (health.latestRegressionState === 'regressed') {
    return t('summary.regressionDetected');
  }
  if (health.evidenceCleanupCandidates > 0) {
    return t('summary.evidenceCleanup', { count: health.evidenceCleanupCandidates });
  }
  if (health.state === 'healthy') {
    return t('summary.healthy');
  }
  return t('summary.attentionNeeded');
}

function scanStatusLabel(status: string, t: TFunction): string {
  switch (status) {
    case 'completed':
      return t('scanStatuses.completed');
    case 'completed_with_failures':
      return t('scanStatuses.completedWithFailures');
    case 'running':
      return t('scanStatuses.running');
    case 'queued':
      return t('scanStatuses.queued');
    case 'failed':
      return t('scanStatuses.failed');
    case 'auth_failed':
      return t('scanStatuses.authFailed');
    default:
      return status.replace(/_/g, ' ');
  }
}

function siteStatusLabel(status: string, t: TFunction): string {
  switch (status) {
    case 'active':
      return t('siteStatus.active');
    case 'paused':
      return t('siteStatus.paused');
    case 'archived':
      return t('siteStatus.archived');
    default:
      return status;
  }
}

function filterRows(rows: SiteRow[], view: SitePortfolioView): SiteRow[] {
  switch (view) {
    case 'queue':
      return rows.filter((row) => row.scan?.status === 'queued' || row.scan?.status === 'running');
    case 'issues':
      return rows.filter((row) => (row.health?.openCriticalIssues ?? row.scan?.criticalIssues ?? 0) > 0);
    case 'evidence':
      return rows.filter((row) => (row.health?.evidenceCleanupCandidates ?? 0) > 0);
    case 'auth':
      return rows.filter((row) => row.health?.authState === 'reauth_required' || row.health?.authState === 'auth_failed');
    case 'schedules':
      return rows.filter((row) => row.site.scanConfig?.isScheduled || !row.site.scanConfig?.isScheduled);
    case 'reports':
      return rows.filter((row) => row.scan !== null);
    case 'portfolio':
    default:
      return rows;
  }
}

function sortRows(rows: SiteRow[], view: SitePortfolioView): SiteRow[] {
  const sorted = [...rows];
  sorted.sort((left, right) => {
    if (view === 'queue') {
      const leftActive = left.scan?.status === 'running' || left.scan?.status === 'queued' ? 1 : 0;
      const rightActive = right.scan?.status === 'running' || right.scan?.status === 'queued' ? 1 : 0;
      return rightActive - leftActive;
    }
    if (view === 'issues') {
      return (right.health?.openCriticalIssues ?? right.scan?.criticalIssues ?? 0) - (left.health?.openCriticalIssues ?? left.scan?.criticalIssues ?? 0);
    }
    if (view === 'evidence') {
      return (right.health?.evidenceCleanupCandidates ?? 0) - (left.health?.evidenceCleanupCandidates ?? 0);
    }
    if (view === 'auth') {
      const priority = (state: SiteHealthSummary['authState'] | null) => state === 'auth_failed' ? 2 : state === 'reauth_required' ? 1 : 0;
      return priority(right.health?.authState ?? null) - priority(left.health?.authState ?? null);
    }
    const leftTime = new Date(left.scan?.completedAt ?? left.scan?.createdAt ?? 0).getTime();
    const rightTime = new Date(right.scan?.completedAt ?? right.scan?.createdAt ?? 0).getTime();
    return rightTime - leftTime;
  });
  return sorted;
}

interface SitesPageProps {
  autoOpenCreate?: boolean;
}

export default function SitesPage({ autoOpenCreate = false }: SitesPageProps) {
  const { t, i18n } = useTranslation(['sites', 'common']);
  const { org } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedView = searchParams.get('view');
  const activeView: SitePortfolioView =
    requestedView === 'queue' ||
    requestedView === 'issues' ||
    requestedView === 'evidence' ||
    requestedView === 'auth' ||
    requestedView === 'schedules' ||
    requestedView === 'reports'
      ? requestedView
      : 'portfolio';

  const [data, setData] = useState<SitesResponse | null>(null);
  const [scanMap, setScanMap] = useState<ScanMap>({});
  const [healthSummary, setHealthSummary] = useState<FleetHealthSummary | null>(null);
  const [manualIssues, setManualIssues] = useState<ManualIssueListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(autoOpenCreate);
  const [showManualIssueModal, setShowManualIssueModal] = useState(false);
  const [editingManualIssue, setEditingManualIssue] = useState<ManualIssueListItem | null>(null);
  const [manualIssueResetSignal, setManualIssueResetSignal] = useState(0);
  const [manualIssueSuccessMessage, setManualIssueSuccessMessage] = useState<string | null>(null);
  const [manualIssueSuccessSignal, setManualIssueSuccessSignal] = useState(0);

  const [siteName, setSiteName] = useState('');
  const [siteUrl, setSiteUrl] = useState('');
  const [wcagLevel, setWcagLevel] = useState<'A' | 'AA' | 'AAA'>('AA');
  const [maxPages, setMaxPages] = useState('50');
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [creatingManualIssue, setCreatingManualIssue] = useState(false);
  const [manualIssueError, setManualIssueError] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const siteNameInputRef = useRef<HTMLInputElement | null>(null);
  const lastFocusedElementRef = useRef<HTMLElement | null>(null);
  const viewMeta = getViewMeta(t);
  const maxPagesLimit = data?.plan?.limits.maxPagesPerScan ?? 10000;

  const loadSites = async () => {
    try {
      const result = await api.get<SitesResponse>('/sites');
      setData(result);
      return result.sites ?? [];
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.loadFailed'));
      return [];
    }
  };

  const loadLatestScans = async (sites: SiteWithConfig[]) => {
    const entries = await Promise.all(
      sites.map(async (site) => {
        try {
          const res = await api.get<{ scans: LatestScan[] }>(`/sites/${site.id}/scans?pageSize=1`);
          return [site.id, res.scans?.[0] ?? null] as [string, LatestScan | null];
        } catch {
          return [site.id, null] as [string, LatestScan | null];
        }
      }),
    );
    setScanMap(Object.fromEntries(entries));
  };

  const loadHealth = async () => {
    try {
      const result = await api.get<HealthSummaryResponse>('/sites/health-summary');
      setHealthSummary(result.summary ?? null);
    } catch {
      setHealthSummary(null);
    }
  };

  const loadManualIssues = async (siteId?: string) => {
    try {
      const result = await api.get<ManualIssuesResponse>(siteId ? `/manual-issues?siteId=${siteId}` : '/manual-issues');
      setManualIssues(result.issues ?? []);
    } catch {
      // Keep the current list visible if a background refresh fails.
    }
  };

  const loadAll = async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true;

    if (!silent) {
      setLoading(true);
    }

    setError(null);
    try {
      const sites = await loadSites();
      await Promise.all([
        loadLatestScans(sites),
        loadHealth(),
        activeView === 'issues' ? loadManualIssues() : Promise.resolve(),
      ]);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    void loadAll();
  }, [activeView]);

  useEffect(() => {
    if (autoOpenCreate) {
      setShowModal(true);
      setCreateError(null);
    }
  }, [autoOpenCreate]);

  useEffect(() => {
    if (showManualIssueModal) {
      setManualIssueError(null);
    }
  }, [showManualIssueModal]);

  const closeManualIssueModal = () => {
    setShowManualIssueModal(false);
    setEditingManualIssue(null);
    setManualIssueSuccessMessage(null);
  };

  const openCreateManualIssueModal = () => {
    setEditingManualIssue(null);
    setManualIssueSuccessMessage(null);
    setShowManualIssueModal(true);
  };

  const openEditManualIssueModal = (issue: ManualIssueListItem) => {
    setEditingManualIssue(issue);
    setManualIssueSuccessMessage(null);
    setShowManualIssueModal(true);
  };

  const manualIssueSiteOptions = useMemo(
    () => (data?.sites ?? []).map((site) => ({
      id: site.id,
      name: site.name,
      baseUrl: site.baseUrl,
    })),
    [data?.sites],
  );

  useEffect(() => {
    return subscribeToWorkspaceDataChanges(() => {
      void loadAll({ silent: true });
    });
  }, [activeView]);

  useEffect(() => {
    if (!showModal) {
      return;
    }

    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement && activeElement !== document.body) {
      lastFocusedElementRef.current = activeElement;
    } else {
      lastFocusedElementRef.current = document.querySelector<HTMLElement>('[data-add-site-trigger="true"]');
    }

    const frame = window.requestAnimationFrame(() => {
      siteNameInputRef.current?.focus();
      siteNameInputRef.current?.select();
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [showModal]);

  const restoreFocusToTrigger = () => {
    const fallbackTrigger = document.querySelector<HTMLElement>('[data-add-site-trigger="true"]');
    const focusTarget =
      lastFocusedElementRef.current && lastFocusedElementRef.current.isConnected
        ? lastFocusedElementRef.current
        : fallbackTrigger;

    if (!focusTarget) {
      return;
    }

    window.requestAnimationFrame(() => {
      if (focusTarget.isConnected) {
        focusTarget.focus();
      }
    });
  };

  const closeCreateModal = () => {
    setShowModal(false);
    setCreateError(null);
    if (autoOpenCreate) {
      navigate('/sites', { replace: true });
    }
    restoreFocusToTrigger();
  };

  const handleModalKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeCreateModal();
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    const focusableElements = getFocusableElements(modalRef.current);
    if (focusableElements.length === 0) {
      event.preventDefault();
      modalRef.current?.focus();
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement as HTMLElement | null;

    if (event.shiftKey) {
      if (!activeElement || activeElement === firstElement || !modalRef.current?.contains(activeElement)) {
        event.preventDefault();
        lastElement.focus();
      }
      return;
    }

    if (!activeElement || activeElement === lastElement || !modalRef.current?.contains(activeElement)) {
      event.preventDefault();
      firstElement.focus();
    }
  };

  const handleCreate = async (event: FormEvent) => {
    event.preventDefault();
    setCreateError(null);
    setSubmitting(true);

    try {
      const requestedMaxPages = parseInt(maxPages, 10) || 50;
      if (requestedMaxPages > maxPagesLimit) {
        setCreateError(t('modal.maxPagesLimitError', { count: maxPagesLimit }));
        return;
      }

      await api.post('/sites', {
        name: siteName,
        baseUrl: siteUrl.startsWith('http') ? siteUrl : `https://${siteUrl}`,
        wcagLevel,
        maxPages: requestedMaxPages,
        maxDepth: 5,
      });

      setSiteName('');
      setSiteUrl('');
      setWcagLevel('AA');
      setMaxPages('50');
      closeCreateModal();
      await loadAll({ silent: true });
      dispatchWorkspaceDataChanged({ kind: 'site-created' });
    } catch (err) {
      setCreateError(err instanceof ApiError ? err.message : t('errors.createFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateManualIssue = async (input: ManualIssueModalSubmitInput) => {
    setCreatingManualIssue(true);
    setManualIssueError(null);
    setManualIssueSuccessMessage(null);

    try {
      const result = await api.post<{ issue: ManualIssueListItem }>('/manual-issues', input);
      setManualIssues((current) => [result.issue, ...current.filter((issue) => issue.id !== result.issue.id)]);
      setManualIssueResetSignal((current) => current + 1);
      setManualIssueSuccessMessage(t('common:manualIssues.createSucceeded'));
      setManualIssueSuccessSignal((current) => current + 1);
      dispatchWorkspaceDataChanged({ kind: 'manual-issue-created', siteId: input.siteId });
      return true;
    } catch (err) {
      setManualIssueError(err instanceof ApiError ? err.message : t('common:manualIssues.createFailed'));
      return false;
    } finally {
      setCreatingManualIssue(false);
    }
  };

  const handleUpdateManualIssue = async (input: ManualIssueModalSubmitInput) => {
    if (!editingManualIssue) {
      return false;
    }

    setCreatingManualIssue(true);
    setManualIssueError(null);
    setManualIssueSuccessMessage(null);

    try {
      const result = await api.patch<{ issue: ManualIssueListItem }>(`/manual-issues/${editingManualIssue.id}`, {
        issueType: input.issueType,
        title: input.title,
        description: input.description,
        affectedPageUrl: input.affectedPageUrl,
        wcagVersion: input.wcagVersion,
        wcagCriterion: input.wcagCriterion,
        recommendedFix: input.recommendedFix,
        reviewBatch: input.reviewBatch,
        severity: input.severity,
        status: input.status,
      });
      setManualIssues((current) => current.map((issue) => (
        issue.id === result.issue.id ? result.issue : issue
      )));
      closeManualIssueModal();
      dispatchWorkspaceDataChanged({ kind: 'manual-issue-updated', siteId: input.siteId });
      return true;
    } catch (err) {
      setManualIssueError(err instanceof ApiError ? err.message : t('common:manualIssues.updateFailed'));
      return false;
    } finally {
      setCreatingManualIssue(false);
    }
  };

  const healthMap = useMemo(() => {
    return Object.fromEntries((healthSummary?.sites ?? []).map((item) => [item.siteId, item])) as Record<string, SiteHealthSummary>;
  }, [healthSummary]);

  const rows = useMemo<SiteRow[]>(() => {
    return (data?.sites ?? []).map((site) => {
      const scan = scanMap[site.id] ?? null;
      const health = healthMap[site.id] ?? null;
      const score = scan && (scan.status === 'completed' || scan.status === 'completed_with_failures')
        ? estimateAccessibilityScore(scan.criticalIssues ?? 0, scan.totalIssues ?? 0)
        : null;

      return {
        site,
        scan,
        health,
        score,
        authLabel: authLabel(health?.authState ?? null, t),
        authVariant: authVariant(health?.authState ?? null),
        trendLabel: trendLabel(health?.latestRegressionState ?? null, t),
        trendVariant: trendVariant(health?.latestRegressionState ?? null),
        quickActionLabel: activeView === 'reports' ? t('actions.openReport') : t('actions.openWorkspace'),
        quickActionTo: activeView === 'reports'
          ? `/sites/${site.id}/compliance`
          : `/sites/${site.id}`,
      };
    });
  }, [activeView, data?.sites, healthMap, scanMap, t, i18n.language]);

  const filteredRows = useMemo(() => sortRows(filterRows(rows, activeView), activeView), [activeView, rows]);

  const metrics = useMemo(() => {
    return {
      sitesCount: rows.length,
      runningCount: rows.filter((row) => row.scan?.status === 'queued' || row.scan?.status === 'running').length,
      atRiskCount: rows.filter((row) => row.health?.state && row.health.state !== 'healthy').length,
      authAttentionCount: rows.filter((row) => row.health?.authState === 'reauth_required' || row.health?.authState === 'auth_failed').length,
      criticalCount: rows.reduce((sum, row) => sum + (row.health?.openCriticalIssues ?? row.scan?.criticalIssues ?? 0), 0),
    };
  }, [rows]);

  const hasActivePortfolioScan = useMemo(
    () => Object.values(scanMap).some((scan) => scan?.status === 'queued' || scan?.status === 'running'),
    [scanMap],
  );

  useEffect(() => {
    if (!hasActivePortfolioScan) {
      return;
    }

    const intervalId = window.setInterval(() => {
      void loadAll({ silent: true });
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, [hasActivePortfolioScan, activeView]);

  const setView = (view: SitePortfolioView) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (view === 'portfolio') {
        next.delete('view');
      } else {
        next.set('view', view);
      }
      return next;
    });
  };

  const viewButtons: Array<{ id: SitePortfolioView; label: string }> = [
    { id: 'portfolio', label: t('views.portfolio.button') },
    { id: 'queue', label: t('views.queue.button') },
    { id: 'issues', label: t('views.issues.button') },
    { id: 'evidence', label: t('views.evidence.button') },
    { id: 'auth', label: t('views.auth.button') },
    { id: 'schedules', label: t('views.schedules.button') },
    { id: 'reports', label: t('views.reports.button') },
  ];

  return (
    <Container maxWidth={1360}>
      <div className="acc-portfolio-surface">
        <PageHeader
          eyebrow={t('header.eyebrow')}
          title={viewMeta[activeView].label}
          subtitle={data?.plan ? t('header.planUsage', {
            plan: getPlanLabel(data.plan.plan),
            used: data.sites.length,
            limit: data.plan.limits.maxSites,
          }) : t('header.subtitle')}
          description={viewMeta[activeView].description}
          action={
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {activeView === 'issues' && (
                <Button onClick={openCreateManualIssueModal}>
                  {t('common:manualIssues.addAction')}
                </Button>
              )}
              <Button variant={activeView === 'issues' ? 'secondary' : 'primary'} onClick={() => setShowModal(true)}>
                {t('header.addSite')}
              </Button>
            </div>
          }
        />

        <div className="acc-command-kpis">
          <StatCard value={metrics.sitesCount} label={t('metrics.sitesMonitored')} />
          <StatCard value={metrics.criticalCount} label={t('metrics.criticalIssues')} accent={metrics.criticalCount > 0 ? 'var(--acc-color-danger)' : undefined} />
          <StatCard value={metrics.runningCount} label={t('metrics.scansRunning')} accent={metrics.runningCount > 0 ? 'var(--acc-color-accent)' : undefined} />
          <StatCard value={metrics.atRiskCount} label={t('metrics.sitesAtRisk')} accent={metrics.atRiskCount > 0 ? 'var(--acc-color-warning)' : undefined} />
          <StatCard value={metrics.authAttentionCount} label={t('metrics.authAttention')} accent={metrics.authAttentionCount > 0 ? 'var(--acc-color-danger)' : undefined} />
        </div>

        <Card className="acc-portfolio-lenses">
          <SectionHeader
            eyebrow={t('lenses.eyebrow')}
            title={t('lenses.title')}
          />
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {viewButtons.map((view) => (
              <button
                key={view.id}
                type="button"
                onClick={() => setView(view.id)}
                className={activeView === view.id ? 'acc-filter-chip acc-filter-chip--active' : 'acc-filter-chip'}
              >
                {view.label}
              </button>
            ))}
          </div>
        </Card>

        {error && (
          <Card>
            <div style={{ color: 'var(--acc-color-danger)', fontSize: 14 }}>{error}</div>
          </Card>
        )}

        {!loading && activeView === 'issues' && data && data.sites.length > 0 && (
          <Card style={{ marginBottom: 20, padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '22px 22px 0' }}>
              <SectionHeader
                eyebrow={t('common:manualIssues.eyebrow')}
                title={t('common:manualIssues.tableTitle')}
                subtitle={t('common:manualIssues.tableDescription')}
                action={
                  <Button size="sm" onClick={openCreateManualIssueModal}>
                    {t('common:manualIssues.addAction')}
                  </Button>
                }
              />
            </div>

            {manualIssues.length === 0 ? (
              <div style={{ padding: '0 22px 22px' }}>
                <EmptyState
                  icon="✍️"
                  title={t('common:manualIssues.emptyTitle')}
                  description={t('common:manualIssues.emptyDescription')}
                  action={
                    <Button onClick={openCreateManualIssueModal}>
                      {t('common:manualIssues.addAction')}
                    </Button>
                  }
                />
              </div>
            ) : (
              <ManualIssuesTable
                issues={manualIssues}
                showSiteColumn
                caption={t('common:manualIssues.tableCaptionPortfolio')}
                exportScopeName={org?.name ?? 'workspace'}
                onEdit={openEditManualIssueModal}
              />
            )}
          </Card>
        )}

        {loading ? (
          <Card>
            <div style={{ color: C.muted }}>{t('loading')}</div>
          </Card>
        ) : data && data.sites.length === 0 ? (
          <EmptyState
            icon="🌐"
            title={t('empty.title')}
            description={t('empty.description')}
            action={<Button onClick={() => setShowModal(true)}>{t('empty.cta')}</Button>}
          />
        ) : filteredRows.length === 0 ? (
          activeView === 'issues' && manualIssues.length > 0 ? null : (
            <EmptyState
              icon="📋"
              title={t('empty.filteredTitle', { view: viewMeta[activeView].label.toLowerCase() })}
              description={t('empty.filteredDescription')}
              action={<Button variant="secondary" onClick={() => setView('portfolio')}>{t('empty.returnToPortfolio')}</Button>}
            />
          )
        ) : (
          <Card className="acc-portfolio-table-shell" style={{ padding: 0, overflow: 'hidden' }}>
            <table style={TABLE.table} className="acc-data-table acc-data-table--sites">
              <thead>
                <tr>
                  <th style={TABLE.th}>{t('table.site')}</th>
                  <th style={TABLE.th}>{t('table.complianceScore')}</th>
                  <th style={TABLE.th}>{t('table.lastScan')}</th>
                  <th style={TABLE.th}>{t('table.authStatus')}</th>
                  <th style={TABLE.th}>{t('table.criticalIssues')}</th>
                  <th style={TABLE.th}>{t('table.trendDelta')}</th>
                  <th style={TABLE.th}>{t('table.quickActions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr key={row.site.id}>
                    <td style={TABLE.td}>
                      <div style={{ display: 'grid', gap: 5 }}>
                        <Link to={`/sites/${row.site.id}`} className="acc-table-primary-link">
                          {row.site.name}
                        </Link>
                        <span className="acc-table-meta" style={{ wordBreak: 'break-all' }}>
                          {row.site.baseUrl}
                        </span>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <Badge variant={row.site.status === 'active' ? 'success' : row.site.status === 'paused' ? 'warning' : 'neutral'}>
                            {siteStatusLabel(row.site.status, t)}
                          </Badge>
                          {row.site.scanConfig?.wcagLevel && (
                            <Badge variant="info">WCAG {row.site.scanConfig.wcagLevel}</Badge>
                          )}
                          <Badge variant={row.site.scanConfig?.isScheduled ? 'info' : 'neutral'}>
                            {row.site.scanConfig?.isScheduled ? t('badges.scheduled') : t('badges.manual')}
                          </Badge>
                        </div>
                      </div>
                    </td>
                    <td style={TABLE.td}>
                      {row.score !== null ? (
                        <div style={{ display: 'grid', gap: 6 }}>
                          <div style={{ fontSize: 30, fontWeight: 820, color: C.text, lineHeight: 1 }}>
                            {row.score}
                          </div>
                          <Badge variant={getScoreVariant(row.score)}>{t(`common:${getScoreLabelKey(row.score)}`)}</Badge>
                        </div>
                      ) : (
                        <div style={{ display: 'grid', gap: 6 }}>
                          <div style={{ fontSize: 24, fontWeight: 800, color: C.muted, lineHeight: 1 }}>—</div>
                          <span style={{ fontSize: 12, color: C.muted }}>{t('table.awaitingCompletedScan')}</span>
                        </div>
                      )}
                    </td>
                    <td style={TABLE.td}>
                      {row.scan ? (
                        <div style={{ display: 'grid', gap: 8 }}>
                          <Badge variant={scanStatusVariant(row.scan.status)}>
                            {scanStatusLabel(row.scan.status, t)}
                          </Badge>
                          <span className="acc-table-meta">
                            {formatDateTime(row.scan.completedAt ?? row.scan.failedAt ?? row.scan.createdAt) ?? t('common:labels.notAvailable')}
                          </span>
                        </div>
                      ) : (
                        <span className="acc-table-meta">{t('table.noScansYet')}</span>
                      )}
                    </td>
                    <td style={TABLE.td}>
                      <div style={{ display: 'grid', gap: 8 }}>
                        <Badge variant={row.authVariant}>{row.authLabel}</Badge>
                        <span className="acc-table-meta">
                          {healthSummaryLabel(row.health, t)}
                        </span>
                      </div>
                    </td>
                    <td style={TABLE.td}>
                      <div style={{ display: 'grid', gap: 6 }}>
                        <div
                          style={{
                            fontSize: 24,
                            fontWeight: 800,
                            color: (row.health?.openCriticalIssues ?? row.scan?.criticalIssues ?? 0) > 0 ? 'var(--acc-color-danger)' : C.text,
                            lineHeight: 1,
                          }}
                        >
                          {row.health?.openCriticalIssues ?? row.scan?.criticalIssues ?? 0}
                        </div>
                        <span className="acc-table-meta">
                          {row.scan ? t('table.totalFindings', { count: row.scan.totalIssues }) : t('table.noFindingsYet')}
                        </span>
                      </div>
                    </td>
                    <td style={TABLE.td}>
                      <div style={{ display: 'grid', gap: 8 }}>
                        <Badge variant={row.trendVariant}>{row.trendLabel}</Badge>
                        <span className="acc-table-meta">
                          {row.health?.state === 'healthy'
                            ? t('table.noRegressionSignal')
                            : healthSummaryLabel(row.health, t)}
                        </span>
                      </div>
                    </td>
                    <td style={TABLE.td}>
                      <div className="acc-table-actions">
                        <Link to={row.quickActionTo} style={{ textDecoration: 'none' }}>
                          <Button size="sm">{row.quickActionLabel}</Button>
                        </Link>
                        <div className="acc-table-tertiary-links">
                          <Link to={`/sites/${row.site.id}`} className="acc-table-tertiary-link">
                            {t('actions.workspace')}
                          </Link>
                          {row.scan && (
                            <Link to={`/scans/${row.scan.id}`} className="acc-table-tertiary-link">
                              {t('actions.latestScan')}
                            </Link>
                          )}
                          <Link to={`/sites/${row.site.id}/compliance`} className="acc-table-tertiary-link">
                            {t('actions.report')}
                          </Link>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>

      {showModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t('modal.ariaLabel')}
          onKeyDown={handleModalKeyDown}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(2, 6, 23, 0.54)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 200,
            padding: 20,
          }}
        >
          <div
            ref={modalRef}
            tabIndex={-1}
            style={{
              background: C.surface,
              borderRadius: 24,
              padding: '34px 36px 30px',
              width: '100%',
              maxWidth: 520,
              boxShadow: 'var(--acc-shadow-lg)',
              border: '1px solid var(--acc-color-border)',
            }}
          >
            <SectionHeader
              eyebrow={t('modal.eyebrow')}
              title={t('modal.title')}
              subtitle={t('modal.subtitle')}
            />

            {createError && (
              <div
                style={{
                  background: 'var(--acc-color-danger-soft)',
                  border: '1px solid rgba(220, 38, 38, 0.18)',
                  borderRadius: 14,
                  padding: '12px 14px',
                  color: 'var(--acc-color-danger)',
                  fontSize: 13,
                  marginBottom: 16,
                }}
              >
                {createError}
              </div>
            )}

            <form onSubmit={handleCreate} noValidate style={{ display: 'grid', gap: 16 }}>
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{t('modal.siteName')}</span>
                <input
                  ref={siteNameInputRef}
                  className="acc-input"
                  value={siteName}
                  onChange={(event) => setSiteName(event.target.value)}
                  placeholder={t('modal.siteNamePlaceholder')}
                  required
                  style={{ background: 'var(--input-bg)', color: 'var(--text)' }}
                />
              </label>

              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{t('modal.baseUrl')}</span>
                <input
                  className="acc-input"
                  value={siteUrl}
                  onChange={(event) => setSiteUrl(event.target.value)}
                  placeholder={t('modal.baseUrlPlaceholder')}
                  required
                  style={{ background: 'var(--input-bg)', color: 'var(--text)' }}
                />
              </label>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{t('modal.wcagLevel')}</span>
                  <select
                    className="acc-select"
                    value={wcagLevel}
                    onChange={(event) => setWcagLevel(event.target.value as 'A' | 'AA' | 'AAA')}
                    style={{ background: 'var(--input-bg)', color: 'var(--text)', colorScheme: 'inherit' }}
                  >
                    <option value="A">{t('modal.wcagOptions.a')}</option>
                    <option value="AA">{t('modal.wcagOptions.aa')}</option>
                    <option value="AAA">{t('modal.wcagOptions.aaa')}</option>
                  </select>
                </label>

                <label style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{t('modal.maxPages')}</span>
                  <input
                    className="acc-input"
                    type="number"
                    min={1}
                    max={maxPagesLimit}
                    value={maxPages}
                    onChange={(event) => setMaxPages(event.target.value)}
                    style={{ background: 'var(--input-bg)', color: 'var(--text)' }}
                  />
                  <span style={{ fontSize: 12, color: C.soft }}>
                    {t('modal.maxPagesHint', { count: maxPagesLimit })}
                  </span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={closeCreateModal}
                >
                  {t('common:actions.cancel')}
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? t('common:actions.creating') : t('modal.createSite')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ManualIssueModal
        open={showManualIssueModal}
        onClose={closeManualIssueModal}
        onSubmit={editingManualIssue ? handleUpdateManualIssue : handleCreateManualIssue}
        submitting={creatingManualIssue}
        error={manualIssueError}
        successMessage={manualIssueSuccessMessage}
        successSignal={manualIssueSuccessSignal}
        mode={editingManualIssue ? 'edit' : 'create'}
          initialValues={editingManualIssue ? {
            siteId: editingManualIssue.siteId,
            issueType: editingManualIssue.issueType === 'usability_issue' ? 'usability_issue' : 'wcag_issue',
            title: editingManualIssue.title,
            description: editingManualIssue.description,
            affectedPageUrl: editingManualIssue.affectedPageUrl,
            wcagVersion: editingManualIssue.wcagVersion,
            wcagCriterion: editingManualIssue.wcagCriterion,
            recommendedFix: editingManualIssue.recommendedFix,
            reviewBatch: editingManualIssue.reviewBatch,
            severity: editingManualIssue.severity,
            status: editingManualIssue.status,
          } : null}
        siteOptions={manualIssueSiteOptions}
        fixedSiteId={editingManualIssue?.siteId ?? null}
        resetSignal={manualIssueResetSignal}
      />
    </Container>
  );
}
